import moment from 'moment';
import { CommonUtility } from '@utility/common';
import { carrierServices } from '@utility/constants/common';
import {
  type ConnectedCarrierAccounts,
  ownAccountLast4,
} from '@utility/own-carrier-account';
import type { FulfillmentProviderSelection } from './shipping-form';

/** One quote from the warehouse: a carrier service in a given package. */
export type ProviderRatePackage = {
  rate?: number | string;
  days?: number | string;
  error?: string;
  /** False when the carrier quotes the rate but cannot print the label. */
  label?: boolean;
  /** The carrier account number that quoted this rate. */
  credentials_id?: string | null;
  /** Whose account that is: `organization`, `platform`, `fulfillment_provider`. */
  credentials_source?: string;
  provider: { key: string; value: string };
  service: { key: string; value: string };
  package: { key: string; value: string };
  /**
   * The package this rate prices: its number in the quote, its size, and, when
   * the quote was asked for in detail, what the warehouse put in it.
   */
  package_id?: number;
  length?: number | string;
  width?: number | string;
  height?: number | string;
  pounds?: number | string;
  ounces?: number | string;
  items?: { item_id: string; qty: number | string; bundle_item_id?: string }[];
};

export type ProviderQuote = {
  loading?: boolean;
  rate?: number | string | null;
  fetchRateError?: string;
  packages?: Record<string, ProviderRatePackage[]>;
};

export type RateOption = {
  key: string;
  selection: FulfillmentProviderSelection;
  serviceName: string;
  carrierName: string;
  icon: string;
  /** The whole fulfillment: every package this service has to carry. */
  rate?: number;
  days?: number;
  /** Arrival estimated from the transit days, weekends left out. */
  delivery?: Date;
  /**
   * Last four digits of the workspace's own carrier account when every package
   * was quoted on it; unset for rates on anyone else's account.
   */
  ownAccountLast4?: string;
  /** The platform's own carrier account paid for this rate. */
  isPlatformRate: boolean;
  isDisabled: boolean;
};

const FALLBACK_ICON = '/images/carrier/fallback.svg';

export function rateOptionKey(selection: FulfillmentProviderSelection): string {
  return selection.service
    ? [selection.provider, selection.service].join('|')
    : 'any';
}

function toNumber(value?: number | string): number | undefined {
  const parsed = CommonUtility.convertToNumber(value);

  return Number.isFinite(parsed) ? parsed : undefined;
}

/** Carriers count transit in business days, so the estimate skips weekends. */
export function deliveryDate(
  days?: number,
  from: Date = new Date(),
): Date | undefined {
  if (!days || days < 1) {
    return undefined;
  }

  const date = moment(from);

  for (let left = days; left > 0; left -= 1) {
    date.add(1, 'day');

    while (date.day() === 0 || date.day() === 6) {
      date.add(1, 'day');
    }
  }

  return date.toDate();
}

function toOption(
  providerId: string,
  carrier: string,
  quotes: ProviderRatePackage[],
  from: Date,
  accounts?: ConnectedCarrierAccounts,
): RateOption {
  const [first] = quotes;
  const rates = quotes.map(quote => toNumber(quote.rate));
  const days = quotes.map(quote => toNumber(quote.days));
  /* Every package has to be priced before the service has a price at all. */
  const rate = rates.every(value => value !== undefined)
    ? rates.reduce((total, value) => (total || 0) + (value || 0), 0)
    : undefined;
  /* The order arrives once its slowest package does. */
  const transit = Math.max(0, ...days.map(value => value || 0)) || undefined;
  /* A row only says "yours" when no package in it was priced elsewhere. */
  const ownAccounts = quotes.map(quote =>
    ownAccountLast4(carrier, quote, accounts),
  );
  const ownAccount = ownAccounts.every(last4 => last4 === ownAccounts[0])
    ? ownAccounts[0]
    : undefined;

  const selection: FulfillmentProviderSelection = {
    id: providerId,
    service: first.service.value,
    packageValue: first.package.value,
    provider: first.provider.value,
    credentials_id: first.credentials_id || null,
  };

  return {
    key: rateOptionKey(selection),
    selection,
    serviceName: first.service.key,
    carrierName:
      carrierServices[carrier]?.text ||
      CommonUtility.toCapitalize(carrier || 'Unknown carrier'),
    icon: carrierServices[carrier]?.icon || FALLBACK_ICON,
    rate,
    days: transit,
    delivery: deliveryDate(transit, from),
    ownAccountLast4: ownAccount,
    isPlatformRate: quotes.some(
      quote => quote.credentials_source === 'platform',
    ),
    isDisabled: quotes.some(quote => quote.label === false),
  };
}

/**
 * Turns a warehouse's quote into the rows the page offers: one per carrier
 * service, cheapest first. A service quoted once per package is charged for all
 * of them, so those quotes add up into a single row. Quotes that failed are
 * dropped — the warehouse keeps its own error.
 */
export function buildRateOptions(
  providerId: string,
  quote?: ProviderQuote,
  from: Date = new Date(),
  accounts?: ConnectedCarrierAccounts,
): RateOption[] {
  const packages = quote?.packages || {};
  const byService = new Map<
    string,
    { carrier: string; quotes: ProviderRatePackage[] }
  >();

  Object.keys(packages).forEach(carrier => {
    (packages[carrier] || [])
      .filter(item => !CommonUtility.isNotEmpty(item.error))
      .forEach(item => {
        const key = `${carrier}|${item.service.value}`;
        const group = byService.get(key);

        if (group) {
          group.quotes.push(item);
        } else {
          byService.set(key, { carrier, quotes: [item] });
        }
      });
  });

  return [...byService.values()]
    .map(group =>
      toOption(providerId, group.carrier, group.quotes, from, accounts),
    )
    .sort(
      (left, right) =>
        Number(left.isDisabled) - Number(right.isDisabled) ||
        (left.rate ?? Infinity) - (right.rate ?? Infinity) ||
        left.serviceName.localeCompare(right.serviceName),
    );
}

/** A rate we do not have is unknown, not free: `currencyFormat` reads it $0.00. */
export function rateText(
  value?: number | string | null,
  currency?: string,
): string {
  const parsed =
    value === null || value === undefined || value === '' ? NaN : Number(value);

  return Number.isFinite(parsed)
    ? `${CommonUtility.currencyFormat(parsed, currency)}`
    : '—';
}

/** The lowest rate on offer, to mark it in the list. */
export function cheapestRateKey(options: RateOption[]): string | undefined {
  const priced = options.filter(
    option => !option.isDisabled && option.rate !== undefined,
  );

  if (priced.length < 2) {
    return undefined;
  }

  return priced.reduce((cheapest, option) =>
    (option.rate || 0) < (cheapest.rate || 0) ? option : cheapest,
  ).key;
}
